/**
 * What this device keeps between visits, in IndexedDB: the events and layouts last seen (so the app
 * opens without a connection) and unsaved changes to a layout (offered back after the browser closes).
 * Cleared when someone signs out.
 */

const NAME = 'venue-cache';
const STORE = 'kv';

let opening: Promise<IDBDatabase> | null = null;

function db(): Promise<IDBDatabase> {
  opening ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return opening;
}

async function run<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T | undefined> {
  try {
    const d = await db();
    return await new Promise<T>((resolve, reject) => {
      const req = work(d.transaction(STORE, mode).objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } catch {
    // private mode or storage blocked: the app works, it just keeps nothing between visits
    return undefined;
  }
}

export function cacheGet<T>(key: string): Promise<T | undefined> {
  return run<T>('readonly', (s) => s.get(key) as IDBRequest<T>);
}

export async function cacheSet(key: string, value: unknown): Promise<void> {
  await run('readwrite', (s) => s.put(value, key));
}

export async function cacheDelete(key: string): Promise<void> {
  await run('readwrite', (s) => s.delete(key));
}

export async function cacheKeys(prefix: string): Promise<string[]> {
  const keys = (await run<IDBValidKey[]>('readonly', (s) => s.getAllKeys())) ?? [];
  return keys.map(String).filter((k) => k.startsWith(prefix));
}

export async function cacheClear(): Promise<void> {
  await run('readwrite', (s) => s.clear());
}
